import type { QuestionnaireAnswers } from '@/features/questionnaires/elements/answers';

import type { SessionStepDto } from '../../schemas/sessionSchemas';

/**
 * The Questionnaire step. Matched on step type alone — like Document Template, and unlike the
 * verification steps whose display names vary, this one is only ever created by the builder's
 * Questionnaire tile.
 */
export function isQuestionnaireStep(step: SessionStepDto): boolean {
  return step.stepType === 'questionnaire';
}

/**
 * What a completed Questionnaire step stores in its `inputJson`.
 *
 * Shape: `{ answers: { <sectionId>: { <fieldId>: value } } }`.
 *
 * WHY NESTED UNDER `answers` rather than being the whole document. It leaves room for anything else
 * the step needs to record later (how long it took, which section was abandoned) without those keys
 * being indistinguishable from a section id. Flat, a future `startedAt` would look exactly like a
 * section whose answers happened to be a string.
 *
 * WHY KEYED BY ID AND NOT BY LABEL. Labels are edited — an admin fixing a typo in a question would
 * otherwise orphan every answer already recorded against the old wording. Ids are stable for the
 * life of the field, so a stored answer stays attached to the question that asked it. The label is
 * recovered for display by reading the questionnaire, which is fetched separately anyway.
 */
export interface QuestionnaireStepInput {
  answers: QuestionnaireAnswers;
}

/**
 * Reads the answers back out of a stored step.
 *
 * Tolerant: returns an empty set for absent, unparseable, or wrongly-shaped JSON. This runs on the
 * admin's session view against rows any past build may have written, so "the shape is wrong" is a
 * real state — and a detail panel that throws takes the whole session page with it.
 */
export function parseQuestionnaireAnswers(inputJson: string | null): QuestionnaireAnswers {
  if (!inputJson) return {};

  try {
    const parsed: unknown = JSON.parse(inputJson);
    if (!parsed || typeof parsed !== 'object') return {};

    const answers = (parsed as { answers?: unknown }).answers;
    if (!answers || typeof answers !== 'object' || Array.isArray(answers)) return {};

    // Only object-valued sections are kept. A section whose value is a string or a number is not
    // an answer set, and passing it on would have the renderer read properties off a primitive.
    const out: QuestionnaireAnswers = {};
    for (const [sectionId, sectionAnswers] of Object.entries(answers as Record<string, unknown>)) {
      if (sectionAnswers && typeof sectionAnswers === 'object' && !Array.isArray(sectionAnswers)) {
        out[sectionId] = sectionAnswers as Record<string, unknown>;
      }
    }
    return out;
  } catch {
    return {};
  }
}
